<template>
  <div class="create-invoice-page mt-4 mb-4">
    <b-alert v-if="submitError" variant="danger" show dismissible class="mb-3">
      {{ submitError }}
    </b-alert>

    <!-- Step 0: Options Selection -->
    <b-card v-if="currentStep === 0" no-body class="mb-3 shadow-sm border-0 overflow-hidden">
      <b-card-body>
        <p class="text-muted mb-4">Choose how you would like to create the invoice:</p>

        <b-row>
          <b-col md="6" class="mb-3">
            <b-card
              no-body
              class="h-100 text-center border"
              :class="{ 'border-primary shadow-sm': selectedOption === 'create', 'border-light': selectedOption !== 'create' }"
              @click="selectedOption = 'create'"
            >
              <b-card-body class="d-flex flex-column justify-content-between py-4">
                <div>
                  <feather-icon icon="FileTextIcon" size="48" class="text-primary mb-3" />
                  <h5 class="font-weight-bold mb-2">Create Invoice</h5>
                  <p class="text-muted small mb-0">Manually create invoice by selecting client, template, and entering line items</p>
                </div>
                <b-button
                  variant="primary"
                  class="mt-3"
                  @click.stop="goToCreateInvoice"
                  :disabled="selectedOption !== 'create'"
                >
                  Create Invoice
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
          <b-col md="6" class="mb-3">
            <b-card
              no-body
              class="h-100 text-center border"
              :class="{ 'border-success shadow-sm': selectedOption === 'upload', 'border-light': selectedOption !== 'upload' }"
              @click="selectedOption = 'upload'"
            >
              <b-card-body class="d-flex flex-column justify-content-between py-4">
                <div>
                  <feather-icon icon="UploadIcon" size="48" class="text-success mb-3" />
                  <h5 class="font-weight-bold mb-2">Upload Invoice</h5>
                  <p class="text-muted small mb-0">Upload an existing invoice PDF and extract all data automatically</p>
                </div>
                <b-button
                  variant="success"
                  class="mt-3"
                  @click.stop="goToUploadInvoice"
                  :disabled="selectedOption !== 'upload'"
                >
                  Upload Invoice
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <div class="text-center mt-3">
          <b-button variant="outline-secondary" @click="$router.go(-1)">
            Cancel
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- Step 1: PDF Upload -->
    <b-card v-if="currentStep === 1" no-body class="mb-3 shadow-sm border-0 overflow-hidden">
      <b-card-body>
        <h5 class="mb-3 font-weight-bold">Step 1: Upload Invoice PDF</h5>
        <b-form-group label="Upload Invoice PDF *" label-for="invoice-file-upload" label-class="font-weight-medium">
          <b-form-file
            id="invoice-file-upload"
            v-model="invoiceFile"
            placeholder="Choose a PDF file or drop it here.."
            drop-placeholder="Drop PDF file here.."
            browse-text="Browse"
            accept="application/pdf"
            class="border"
            @change="onFileChange"
          />
          <small class="text-muted d-block mt-1">Please upload a valid invoice PDF file</small>
          <small v-if="error.invoiceFile" class="text-danger d-block mt-1">{{ error.invoiceFile }}</small>
        </b-form-group>

        <b-alert v-if="parsingError" variant="danger" show class="mt-2">
          {{ parsingError }}
        </b-alert>

        <div v-if="loading" class="text-center py-3">
          <b-spinner variant="primary" class="mr-2" />
          <span class="text-muted">Parsing PDF and extracting data...</span>
        </div>

        <b-alert v-if="extractedData && !loading && dataExtractedFromUpload" variant="success" show class="mt-2">
          <strong>PDF parsed successfully!</strong> Extracted data has been saved. Click "Next" to proceed.
        </b-alert>

        <div class="mt-4">
          <b-button
            variant="primary"
            @click="processPDFUpload"
            :disabled="!invoiceFile || loading"
          >
            <b-spinner v-if="loading" small class="mr-1" />
            Upload & Extract Data
          </b-button>
          <b-button variant="outline-secondary" @click="currentStep = 0" class="ml-2">
            Back to Options
          </b-button>
          <b-button variant="secondary" @click="$router.go(-1)" class="ml-2">
            Cancel
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- Step 2: Form with Extracted Data -->
    <b-card v-if="currentStep === 2" no-body class="shadow-sm border-0 overflow-hidden">
      <b-card-body>
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3">
        <h5 class="mb-2 mb-md-0 font-weight-bold">Step 2: Review and Complete Invoice Details</h5>
        <b-button variant="outline-secondary" size="sm" @click="currentStep = 1">
          ← Back to Upload
        </b-button>
      </div>
      <b-form @submit.prevent="onSubmit">
        <!-- Basic Invoice Information -->
        <div class="mb-2">
          <h6 class="text-muted text-uppercase small font-weight-bold mb-2">Invoice Basic Details</h6>
          <b-row>
            <b-col cols="12" md="6" lg="2" class="mb-2">
              <b-form-group label="Document Type" label-class="font-weight-medium">
                <v-select
                  v-model="invoiceDocumentType"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="invoiceDocumentTypeOptions"
                  :reduce="o => o.value"
                  placeholder="Tax Invoice"
                />
                <small v-if="invoiceDocumentType === 'Proforma Invoice'" class="text-warning">
                  <feather-icon icon="InfoIcon" size="11" class="mr-25" />Proforma — not included in GSTR-1
                </small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="2" class="mb-2">
              <b-form-group id="invoice-id-group" label="Invoice ID *" label-for="invoice-id" label-class="font-weight-medium">
                <b-form-input
                  id="invoice-id"
                  class="form-control text-uppercase"
                  v-model="invoiceId"
                  autofocus
                  placeholder="SH100"
                  trim
                />
                <small v-if="!invoiceId && error.invoiceId" class="text-danger">{{ error.invoiceId }}</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-date-group" label="Invoice Date *" label-for="invoice-date" label-class="font-weight-medium">
                <flat-pickr
                  class="form-control"
                  id="invoice-date"
                  v-model="invoiceDate"
                  placeholder="YYYY-MM-DD"
                  :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                />
                <small v-if="!invoiceDate && error.invoiceDate" class="text-danger">{{ error.invoiceDate }}</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-category" label="Invoice Category *" label-for="invoice-category" label-class="font-weight-medium">
                <v-select
                  v-model="invoiceCategory"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  taggable
                  push-tags
                  placeholder="Select Category"
                  :options="categories"
                  :reduce="(option) => option.title"
                />
                <small v-if="!invoiceCategory && error.invoiceCategory" class="text-danger">{{ error.invoiceCategory }}</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-location" label="Invoice Location *" label-for="invoice-location" label-class="font-weight-medium">
                <v-select
                  v-model="invoiceLocation"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="locations"
                  placeholder="Select Location"
                  :reduce="(option) => option.title"
                />
                <small v-if="!invoiceLocation && error.invoiceLocation" class="text-danger">{{ error.invoiceLocation }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-vertical" label="Vertical *" label-for="invoice-vertical" label-class="font-weight-medium">
                <v-select
                  v-model="invoiceVertical"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="verticalOptions"
                  placeholder="Select Vertical"
                  :reduce="(option) => option.title"
                />
                <small v-if="!invoiceVertical && error.invoiceVertical" class="text-danger">{{ error.invoiceVertical }}</small>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-sattva-gst" label="Sattva GST" label-for="invoice-sattva-gst" label-class="font-weight-medium">
                <b-form-input
                  id="invoice-sattva-gst"
                  v-model="invoiceSattvaGST"
                  class="form-control text-uppercase bg-light"
                  placeholder="Enter Sattva GST Number"
                  readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" lg="3" class="mb-2">
              <b-form-group id="invoice-billing-month" label="Billing Month" label-for="invoice-billing-month" label-class="font-weight-medium">
                <b-form-select
                  id="invoice-billing-month"
                  v-model="invoiceBillingMonth"
                  class="form-control"
                  :options="billingMonthOptions"
                  placeholder="Select Billing Month"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Client - Always visible -->
        <div class="border rounded p-2 mb-2">
          <h6 class="text-muted text-uppercase small font-weight-bold mb-2">Client</h6>
          <b-row>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group id="invoice-client" label="Client *" label-for="invoice-client" label-class="font-weight-medium">
                <v-select
                  v-model="invoiceClient"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="clients"
                  placeholder="Select Client"
                  :reduce="(option) => option.title"
                />
                <small v-if="!invoiceClient && error.invoiceClient" class="text-danger">{{ error.invoiceClient }}</small>
                <span v-if="invoiceClient && invoiceCategory && (invoiceCategory === 'Recruitment' || invoiceCategory === 'Contractual')" class="text-primary ml-2" style="cursor: pointer;" @click="getCandidateListModal">
                  <u>Get Candidate List</u>
                </span>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group id="invoice-client-gst" label="Client GST *" label-for="invoice-client-gst" label-class="font-weight-medium">
                <b-form-input
                  id="invoice-client-gst"
                  v-model="invoiceClientGST"
                  class="form-control text-uppercase"
                  placeholder="Enter Client GST Number"
                />
                <small v-if="!invoiceClientGST && error.invoiceClientGST" class="text-danger">{{ error.invoiceClientGST }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <b-button v-b-toggle.client-details variant="link" size="sm" class="p-0 text-decoration-none small text-muted d-block mb-1">
            + Address, Place of Supply, State, PO <feather-icon :icon="clientDetailsOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          </b-button>
          <b-collapse id="client-details" v-model="clientDetailsOpen">
          <b-row>
            <b-col cols="12" class="mb-2">
              <b-form-group label="Client Address" label-class="font-weight-medium">
                <b-form-textarea v-model="clientAddress" class="form-control" rows="2" placeholder="Enter client address" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group label="Place of Supply" label-class="font-weight-medium">
                <v-select
                  v-model="placeOfSupply"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  label="title"
                  :options="locations"
                  placeholder="Select Place of Supply"
                  :reduce="(option) => option.title"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group label="State" label-class="font-weight-medium">
                <b-form-input v-model="clientState" class="form-control" placeholder="Enter state" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group id="invoice-po-number" label="PO/WO Number" label-for="invoice-po-number" label-class="font-weight-medium">
                <b-form-input
                  id="invoice-po-number"
                  v-model="invoicePONumber"
                  class="form-control"
                  placeholder="Enter PO/WO Number"
                />
              </b-form-group>
            </b-col>
          </b-row>
          </b-collapse>
        </div>

        <!-- GST Type, PDF, Supporting Doc, Description - Always visible -->
        <b-row class="mb-2">
          <b-col cols="12" md="3" class="mb-2">
            <b-form-group id="invoice-gst" label="Invoice GST Type *" label-for="invoice-gst" label-class="font-weight-medium">
              <v-select
                v-model="invoiceGST"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                label="title"
                :options="invoiceGSTOptions"
                placeholder="Select GST Type"
                :reduce="(option) => option.title"
              />
              <small v-if="!invoiceGST && error.invoiceGST" class="text-danger">{{ error.invoiceGST }}</small>
              <!-- Auto-suggest based on state codes -->
              <small v-if="gstTypeSuggestion" :class="invoiceGST && invoiceGST !== gstTypeSuggestion ? 'text-warning' : 'text-info'">
                <feather-icon icon="InfoIcon" size="11" class="mr-25" />
                {{ invoiceGST && invoiceGST !== gstTypeSuggestion ? '⚠ Suggested: ' + gstTypeSuggestion + ' (state codes differ)' : '✓ Matches state code logic (' + gstTypeSuggestion + ')' }}
              </small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <b-form-group label="Invoice PDF" label-class="font-weight-medium">
              <div v-if="invoiceFile" class="d-flex align-items-center">
                <small class="text-success mr-2">✓ {{ invoiceFile.name }}</small>
                <b-button variant="link" size="sm" class="p-0" @click="invoiceFile = null">Change</b-button>
              </div>
              <b-form-file
                v-else
                v-model="invoiceFile"
                class="form-control"
                placeholder="Choose PDF..."
                drop-placeholder="Drop PDF here"
                accept="application/pdf"
                @change="onFileChange"
              />
              <small v-if="error.invoiceFile" class="text-danger">{{ error.invoiceFile }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <b-form-group label="Supporting Document *" label-class="font-weight-medium">
              <div v-if="supportingDocument" class="d-flex align-items-center">
                <feather-icon icon="PaperclipIcon" size="13" class="text-success mr-1" />
                <small class="text-success mr-2 text-truncate" style="max-width:130px">{{ supportingDocument.name }}</small>
                <b-button variant="link" size="sm" class="p-0 text-danger" @click="supportingDocument = null">✕</b-button>
              </div>
              <b-form-file
                v-else
                v-model="supportingDocument"
                class="form-control"
                placeholder="PO / Approval / WO..."
                drop-placeholder="Drop file here"
                :accept="supportingDocAccept"
              />
              <small class="text-muted">Required — PDF, PNG, JPG, TXT, XLS, XLSX, or CSV (PO copy, email approval, work order, etc.).</small>
              <small v-if="error.supportingDocument" class="text-danger d-block">{{ error.supportingDocument }}</small>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="3" class="mb-2">
            <b-form-group id="invoice-description" label="Description" label-for="invoice-description" label-class="font-weight-medium">
              <b-form-input id="invoice-description" v-model="invoiceDescription" class="form-control" placeholder="Optional" />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Sattva Company Information (from master table) - Collapsible -->
        <div class="border rounded p-2 mb-2 bg-light">
          <b-button v-b-toggle.sattva-company variant="link" size="sm" class="p-0 text-decoration-none font-weight-bold small d-block">
            + Sattva Company Information <feather-icon :icon="sattvaCompanyOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          </b-button>
          <b-collapse id="sattva-company" v-model="sattvaCompanyOpen">
          <b-row>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group label="Company Name" label-class="font-weight-medium">
                <b-form-input v-model="sattvaCompanyName" class="bg-light" readonly />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group label="PAN Number" label-class="font-weight-medium">
                <b-form-input v-model="sattvaPAN" class="bg-light" readonly />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="mb-2">
              <b-form-group label="Address" label-class="font-weight-medium">
                <b-form-textarea v-model="sattvaAddress" rows="2" class="bg-light" readonly />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group label="Bank Name" label-class="font-weight-medium">
                <b-form-input v-model="sattvaBankName" class="bg-light" readonly />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group label="Account Type" label-class="font-weight-medium">
                <b-form-select v-model="sattvaAccountType" class="form-control" :options="accountTypeOptions" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group label="Account Name" label-class="font-weight-medium">
                <b-form-input v-model="sattvaAccountName" class="bg-light" readonly />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group label="IFSC Code" label-class="font-weight-medium">
                <b-form-input v-model="sattvaIFSC" class="bg-light" readonly />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group label="SAC Code" label-class="font-weight-medium">
                <b-form-select v-model="sattvaSACCode" class="form-control" :options="sacCodeOptions" />
              </b-form-group>
            </b-col>
          </b-row>
          </b-collapse>
        </div>

        <!-- Rate Card Integration -->
        <div v-if="showRateCard" class="border rounded p-2 mb-2">
          <h6 class="mb-2 font-weight-bold small">Rate Card Information</h6>
          <b-row>
            <b-col cols="12" md="6" class="mb-2">
              <b-form-group label="Pricing Model" label-class="font-weight-medium">
                <b-form-select v-model="selectedPaymentModel" class="form-control" :options="paymentModelOptions" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6" class="mb-2" v-if="selectedPaymentModel === 'Fixed Invoice'">
              <b-form-group label="Percentage" label-class="font-weight-medium">
                <b-form-input v-model="fixedInvoicePercentage" class="form-control" type="number" step="0.01" />
              </b-form-group>
            </b-col>
          </b-row>
          <div v-if="rateCardData" class="mt-2">
            <b-alert variant="info" show class="mb-0">
              Rate Card: {{ rateCardData.description }}
            </b-alert>
          </div>
        </div>

        <!-- Payment Terms & TDS - Collapsible -->
        <div class="border rounded p-2 mb-2">
          <b-button v-b-toggle.payment-tds variant="link" size="sm" class="p-0 text-decoration-none font-weight-bold small d-block">
            + Payment Terms & TDS <feather-icon :icon="paymentTdsOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          </b-button>
          <b-collapse id="payment-tds" v-model="paymentTdsOpen">
          <b-row>
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group>
                <b-form-checkbox v-model="isCollectNPay">Is Collect N Pay Model?</b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2" v-if="isCollectNPay">
              <b-form-group label="Is Invoice Already Approved?" label-class="font-weight-medium">
                <b-form-radio-group v-model="isCollectNPayApproved" :options="[{ text: 'Yes', value: true }, { text: 'No', value: false }]" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2" v-if="isCollectNPay">
              <b-form-group label="Pay Date" label-class="font-weight-medium">
                <flat-pickr
                  class="form-control"
                  v-model="payDate"
                  :config="{ enableTime: false, dateFormat: 'Y-m-d' }"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2" v-if="isCollectNPay">
              <b-form-group label="Credit Period" label-class="font-weight-medium">
                <b-form-select v-model="creditPeriod" class="form-control" :options="creditPeriodOptions" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row v-if="isCollectNPay && payDate && creditPeriod">
            <b-col>
              <b-alert variant="info" show class="mb-0">
                Due Date: {{ calculateDueDate }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col cols="12" md="4" class="mb-2">
              <b-form-group>
                <b-form-checkbox v-model="tdsApplicable">TDS Applicable?</b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" class="mb-2" v-if="tdsApplicable">
              <b-form-group label="TDS Percentage" label-class="font-weight-medium">
                <b-form-select v-model="tdsPercentage" class="form-control" :options="tdsPercentageOptions" />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="4" v-if="tdsApplicable && tdsPercentage">
              <b-form-group label="TDS Amount" label-class="font-weight-medium">
                <b-form-input v-model="tdsAmount" class="form-control bg-light" readonly />
              </b-form-group>
            </b-col>
          </b-row>
          </b-collapse>
        </div>

        <!-- Candidates: Single table + Calculation (Screenshot 2 layout) -->
        <div v-if="showInvoiceCandidateList || (invoiceClient && invoiceCategory && (invoiceCategory === 'Recruitment' || invoiceCategory === 'Contractual'))" class="border rounded p-2 mb-2">
          <h6 class="text-muted text-uppercase small font-weight-bold mb-2">
            Candidates
            <b-badge v-if="isRpoTemplate" variant="light-info" class="ml-2">RPO format</b-badge>
          </h6>
          <b-alert variant="light" show class="mb-2 small border">
            <strong class="text-dark">Billing when someone is not in ATS or status is not Joined:</strong>
            use <em>Add manual line (from PDF)</em> and enter the name, ref / role, and amount as on the client invoice.
            You can mix ATS-linked rows and manual rows. When the candidate appears in ATS later, finance can reconcile in the tracker.
          </b-alert>
          <div v-if="!showInvoiceCandidateList" class="d-flex flex-wrap align-items-center mb-2">
            <b-button variant="outline-primary" size="sm" @click="getCandidateListModal" class="mr-2 mb-1">Get Candidate List (ATS)</b-button>
            <b-button variant="outline-secondary" size="sm" @click="addManualInvoiceLine" class="mb-1">
              <feather-icon icon="PlusIcon" size="14" class="mr-50" />
              Add manual line (from PDF)
            </b-button>
          </div>
          <template v-else>
            <div class="d-flex flex-wrap mb-2">
              <b-button variant="outline-primary" size="sm" @click="getCandidateListModal" class="mr-2 mb-1">Add from ATS</b-button>
              <b-button variant="outline-secondary" size="sm" @click="addManualInvoiceLine" class="mb-1">
                <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                Add manual line
              </b-button>
            </div>
            <b-table
              :items="invoiceCandidateList"
              :fields="effectiveCandidateFields"
              striped
              hover
              responsive
              small
              class="mb-2"
            >
              <template #cell(candidate_name)="{ item }">
                <b-form-input
                  v-if="!isRpoTemplate && item.isManualLine"
                  v-model="item.candidate_name"
                  size="sm"
                  placeholder="Name as on invoice"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.candidate_name }}</span>
              </template>
              <template #cell(position_name)="{ item }">
                <b-form-input
                  v-if="!isRpoTemplate && item.isManualLine"
                  v-model="item.position_name"
                  size="sm"
                  placeholder="Role / ref."
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.position_name }}</span>
              </template>
              <template #cell(employee_id)="{ item }">
                <b-form-input
                  v-if="isRpoTemplate && item.isManualLine"
                  v-model="item.employee_id"
                  size="sm"
                  placeholder="Employee / ref. ID"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.employee_id || item.cid || '' }}</span>
              </template>
              <template #cell(employee_name)="{ item }">
                <b-form-input
                  v-if="isRpoTemplate && item.isManualLine"
                  v-model="item.employee_name"
                  size="sm"
                  placeholder="Name as on invoice"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.employee_name || item.candidate_name }}</span>
              </template>
              <template #cell(doj)="{ item }">
                <b-form-input
                  v-if="isRpoTemplate && item.isManualLine"
                  v-model="item.doj"
                  size="sm"
                  placeholder="DOJ if known"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.doj || item.joining_date || '' }}</span>
              </template>
              <template #cell(bu)="{ item }">
                <b-form-input
                  v-if="isRpoTemplate && item.isManualLine"
                  v-model="item.bu"
                  size="sm"
                  placeholder="BU"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.bu || item.position_name || '' }}</span>
              </template>
              <template #cell(geo)="{ item }">
                <b-form-input
                  v-if="isRpoTemplate && item.isManualLine"
                  v-model="item.geo"
                  size="sm"
                  placeholder="Geo"
                  class="form-control"
                />
                <span v-else class="text-muted">{{ item.geo || '' }}</span>
              </template>
              <template #cell(base_amount)="{ item }">
                <b-form-input
                  v-model.number="item.base_amount"
                  type="number"
                  step="0.01"
                  size="sm"
                  class="form-control text-right"
                  @input="calculateTotalBaseAmount"
                />
              </template>
              <template #cell(_actions)="{ index }">
                <div class="d-flex align-items-center" style="gap:4px">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="p-0 px-1"
                    style="font-size:11px"
                    @click="openTagCandidate(index)"
                  >
                    <feather-icon icon="UserCheckIcon" size="12" class="mr-1" />
                    <span v-if="invoiceCandidateList[index] && invoiceCandidateList[index].tagged_status === 'MANUAL'" class="text-success">Tagged</span>
                    <span v-else>Tag</span>
                  </b-button>
                  <b-button
                    v-if="invoiceCandidateList[index] && invoiceCandidateList[index].isManualLine"
                    variant="link"
                    size="sm"
                    class="p-0 text-danger"
                    @click="removeManualInvoiceLine(index)"
                  >
                    Remove
                  </b-button>
                </div>
              </template>
            </b-table>
            <!-- Calculation row (like Screenshot 2) -->
            <div class="border-top pt-2 mt-2 bg-light rounded p-2">
              <b-row class="align-items-center small">
                <b-col cols="6" md="3"><span class="text-muted">Base:</span> <b>{{ formatCurrency(totalBaseAmount) }}</b></b-col>
                <b-col cols="6" md="3"><span class="text-muted">GST:</span> <b>{{ formatCurrency(totalGST) }}</b></b-col>
                <b-col cols="6" md="3"><span class="text-muted">TDS:</span> <b>{{ formatCurrency(tdsAmount) }}</b></b-col>
                <b-col cols="6" md="3"><span class="text-muted">Total:</span> <b>{{ formatCurrency(totalAmountWithGSTTDS) }}</b></b-col>
              </b-row>
            </div>
          </template>
        </div>

        <!-- Action Buttons -->
        <b-row class="mb-2">
          <b-col cols="12" class="d-flex flex-wrap" style="gap:8px">
            <b-button type="button" variant="outline-primary" @click="showPreviewModal = true" :disabled="!isFormValid || loading">
              <feather-icon icon="EyeIcon" size="14" class="mr-1" />
              Preview
            </b-button>
            <b-button type="button" variant="secondary" @click="saveDraft" :disabled="loading">
              <b-spinner v-if="loading && submitAction === 'draft'" small class="mr-1" />
              <feather-icon icon="SaveIcon" size="14" class="mr-1" />
              Save Draft
            </b-button>
            <b-button type="submit" variant="primary" :disabled="!isFormValid || loading" @click="submitAction = 'submit_approval'">
              <b-spinner v-if="loading && submitAction === 'submit_approval'" small class="mr-1" />
              <feather-icon icon="SendIcon" size="14" class="mr-1" />
              Submit for Approval
            </b-button>
            <b-button type="button" variant="secondary" @click="currentStep = 1" class="ml-auto">
              ← Back to Upload
            </b-button>
            <b-button type="button" variant="outline-secondary" @click="$router.go(-1)">
              Cancel
            </b-button>
          </b-col>
        </b-row>
        <!-- Approval status notice -->
        <b-alert v-if="invoiceStatus === 'Draft'" variant="info" show class="small py-1 px-2">
          <feather-icon icon="InfoIcon" size="13" class="mr-1" />
          Invoice saved as <strong>Draft</strong>. Use <em>Submit for Approval</em> to send it to an approver.
        </b-alert>
        <b-alert v-if="invoiceStatus === 'Pending Approval'" variant="warning" show class="small py-1 px-2">
          <feather-icon icon="ClockIcon" size="13" class="mr-1" />
          Invoice is <strong>Pending Approval</strong>. Awaiting approver action.
        </b-alert>
        
        <!-- Display Extracted Data Summary - Collapsed by default -->
        <b-card v-if="extractedData" class="mt-2 shadow-sm border-0">
          <b-button v-b-toggle.extracted-summary variant="link" size="sm" class="p-0 text-decoration-none font-weight-bold d-block">
            + Extracted Data Summary <feather-icon :icon="extractedSummaryOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" />
          </b-button>
          <b-collapse id="extracted-summary" v-model="extractedSummaryOpen">
          <div class="d-flex justify-content-between align-items-center mb-1 mt-2">
            <h6 class="mb-0">Extracted Data Summary</h6>
            <b-button variant="outline-primary" size="sm" @click="downloadExtractedData">
              <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
              Download JSON
            </b-button>
          </div>
          
          <b-row>
            <b-col md="6">
              <b-card class="mb-2">
                <h6 class="text-primary">Basic Information</h6>
                <small><strong>Invoice ID:</strong> {{ extractedData.invoiceId || 'N/A' }}</small><br>
                <small><strong>Invoice Date:</strong> {{ extractedData.invoiceDate || 'N/A' }}</small><br>
                <small><strong>PO Number:</strong> {{ extractedData.invoicePONumber || 'N/A' }}</small><br>
                <small><strong>Billing Month:</strong> {{ extractedData.billingMonth || 'N/A' }}</small><br>
                <small><strong>Status:</strong> {{ extractedData.invoiceStatus || 'N/A' }}</small>
              </b-card>
            </b-col>
            <b-col md="6">
              <b-card class="mb-2">
                <h6 class="text-success">Financial Details</h6>
                <small><strong>Base Amount:</strong> ₹{{ formatIndianNumber(extractedData.baseAmount || 0) }}</small><br>
                <small><strong>Total GST:</strong> ₹{{ formatIndianNumber(extractedData.totalGST || 0) }}</small><br>
                <small><strong>Total Amount:</strong> ₹{{ formatIndianNumber(extractedData.totalAmount || 0) }}</small><br>
                <small><strong>TDS Amount:</strong> ₹{{ formatIndianNumber(extractedData.tdsAmount || 0) }}</small><br>
                <small><strong>Pending Amount:</strong> ₹{{ formatIndianNumber(extractedData.pendingAmount || 0) }}</small>
              </b-card>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="6">
              <b-card class="mb-2">
                <h6 class="text-info">Client Information</h6>
                <small><strong>Client:</strong> {{ extractedData.clientName || 'N/A' }}</small><br>
                <small><strong>GST:</strong> {{ extractedData.clientGST || 'N/A' }}</small><br>
                <small><strong>State:</strong> {{ extractedData.clientState || 'N/A' }}</small><br>
                <small><strong>Place of Supply:</strong> {{ extractedData.placeOfSupply || 'N/A' }}</small>
              </b-card>
            </b-col>
            <b-col md="6">
              <b-card class="mb-2">
                <h6 class="text-warning">Tax Details</h6>
                <small><strong>GST Type:</strong> {{ extractedData.gstType || 'N/A' }}</small><br>
                <small><strong>CGST:</strong> ₹{{ formatIndianNumber(extractedData.cgstAmount || 0) }} ({{ extractedData.cgstRate || 0 }}%)</small><br>
                <small><strong>SGST:</strong> ₹{{ formatIndianNumber(extractedData.sgstAmount || 0) }} ({{ extractedData.sgstRate || 0 }}%)</small><br>
                <small><strong>IGST:</strong> ₹{{ formatIndianNumber(extractedData.igstAmount || 0) }} ({{ extractedData.igstRate || 0 }}%)</small>
              </b-card>
            </b-col>
          </b-row>
          
          <b-alert variant="info" show class="mb-2">
            <strong>Total Fields Extracted:</strong> {{ extractedDataCanonicalFieldCount }} fields
            <br>
            <small>Click "Download JSON" to view complete extracted data</small>
          </b-alert>
          
          <div class="mt-2">
            <b-button v-b-toggle.extracted-data-collapse variant="outline-secondary" size="sm" class="mb-2">
              View Full JSON Data
            </b-button>
            <b-collapse id="extracted-data-collapse">
              <pre class="bg-light p-3 rounded border overflow-auto" style="max-height: 400px; font-size: 12px;">{{ extractedDataCanonicalJson }}</pre>
            </b-collapse>
          </div>
          </b-collapse>
        </b-card>
      </b-form>
      </b-card-body>
    </b-card>

    <!-- Invoice Preview Modal -->
    <b-modal
      id="invoice-preview-modal"
      v-model="showPreviewModal"
      title="Invoice Preview"
      size="lg"
      footer-class="justify-content-between"
      @hidden="showPreviewModal = false"
    >
      <div v-if="showPreviewModal" class="invoice-preview-content">
        <b-card no-body class="border-0 mb-2">
          <b-card-body class="p-3">
            <h6 class="text-uppercase text-muted small font-weight-bold mb-2">Basic Details</h6>
            <b-row>
              <b-col md="6"><strong>Invoice ID:</strong> {{ invoiceId }}</b-col>
              <b-col md="6"><strong>Invoice Date:</strong> {{ invoiceDate }}</b-col>
              <b-col md="6"><strong>Client:</strong> {{ invoiceClient }}</b-col>
              <b-col md="6"><strong>Location:</strong> {{ invoiceLocation }}</b-col>
              <b-col md="6"><strong>Category:</strong> {{ invoiceCategory }}</b-col>
              <b-col md="6"><strong>Vertical:</strong> {{ invoiceVertical }}</b-col>
              <b-col md="6"><strong>Billing Month:</strong> {{ invoiceBillingMonth || 'N/A' }}</b-col>
              <b-col md="6"><strong>GST Type:</strong> {{ invoiceGST }}</b-col>
              <b-col md="6"><strong>Client GST:</strong> {{ invoiceClientGST || 'N/A' }}</b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card no-body class="border-0 mb-2">
          <b-card-body class="p-3">
            <h6 class="text-uppercase text-muted small font-weight-bold mb-2">Candidates / Line Items</h6>
            <b-table
              :items="invoiceCandidateList"
              :fields="previewCandidateFields"
              small
              bordered
              responsive
              head-variant="light"
              show-empty
              empty-text="No candidates"
            >
              <template #cell(base_amount)="{ item }">
                {{ formatCurrency(item.base_amount) }}
              </template>
            </b-table>
          </b-card-body>
        </b-card>

        <b-card no-body class="border-0 mb-2">
          <b-card-body class="p-3">
            <h6 class="text-uppercase text-muted small font-weight-bold mb-2">Amount Summary</h6>
            <b-row>
              <b-col md="3"><strong>Base:</strong> {{ formatCurrency(totalBaseAmount) }}</b-col>
              <b-col md="3"><strong>GST:</strong> {{ formatCurrency(totalGST) }}</b-col>
              <b-col md="3"><strong>TDS:</strong> {{ formatCurrency(tdsAmount) }}</b-col>
              <b-col md="3"><strong>Total:</strong> {{ formatCurrency(totalAmountWithGSTTDS) }}</b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <div v-if="invoiceFile" class="small text-muted">
          <feather-icon icon="FileIcon" size="14" /> Invoice PDF: {{ invoiceFile.name }}
        </div>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showPreviewModal = false">
          Back to Edit
        </b-button>
        <b-button variant="secondary" @click="saveDraftFromPreview" :disabled="loading">
          <b-spinner v-if="loading && submitAction === 'draft'" small class="mr-1" />
          <feather-icon icon="SaveIcon" size="14" class="mr-1" />
          Save Draft
        </b-button>
        <b-button variant="primary" @click="confirmSubmitFromPreview" :disabled="loading">
          <b-spinner v-if="loading && submitAction === 'submit_approval'" small class="mr-1" />
          <feather-icon icon="SendIcon" size="14" class="mr-1" />
          Submit for Approval
        </b-button>
      </template>
    </b-modal>

    <!-- Candidate Selection Modal (ATS Joined candidates — Tag uses this too) -->
    <b-modal
      id="candidate-selection-modal"
      ref="candidateModal"
      title="Add Candidates from ATS"
      size="lg"
      @hidden="clearSelected"
    >
      <b-table
        ref="selectableTable"
        :items="candidateList"
        :fields="modalCandidateListFields"
        selectable
        select-mode="multi"
        @row-selected="onRowSelected"
        striped
        hover
        responsive
      >
        <template #cell(selected)="{ rowSelected }">
          <template v-if="rowSelected">
            <span class="text-success">✓</span>
          </template>
          <span v-else></span>
        </template>
      </b-table>
      <div slot="modal-footer">
        <b-button variant="primary" @click="addSelectedCandidates">
          Add Selected
        </b-button>
        <b-button variant="secondary" @click="$refs.candidateModal.hide()">
          Cancel
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormFile,
  BFormCheckbox,
  BTable,
  BSpinner,
  BModal,
  VBModal,
  BAlert,
  BCollapse,
  VBToggle
} from 'bootstrap-vue'
import moment from 'moment'
import { buildBillingMonthOptions } from '@/utils/invoice-date-calculator'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import clientService from '@/services/client-service'
import candidateService from '@/services/candidate-service'
import locationService from '@/services/location-service'
import invoiceService from '@/services/invoice-service'
import invoiceConfigService, { isClientMasterDataIncomplete, normalizeClientMasterData } from '@/services/invoice-config-service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getInvoiceFileSizeError,
  getInvoiceFileTypeError,
  INVOICE_CREATE_ACCEPT_ATTR,
} from '@/utils/invoice-filename'
import { INVOICE_DOCUMENT_TYPE_SELECT_OPTIONS } from '@/utils/invoice-document-types'
/** Key order and field set for `invoice_extracted_data_*.json` downloads (canonical finance / integration format). */
const EXTRACTED_INVOICE_EXPORT_KEYS = [
  'extractedAt', 'fileName', 'fileSize', 'fileType',
  'invoiceId', 'invoiceDate', 'invoicePONumber', 'invoiceNumber', 'invoiceSeries',
  'sattvaGST', 'sattvaCompanyName', 'sattvaAddress', 'sattvaPAN', 'sattvaState', 'sattvaStateCode', 'sattvaPIN', 'sattvaPhone', 'sattvaEmail',
  'sattvaBankName', 'sattvaAccountNumber', 'sattvaIFSC', 'sattvaSACCode',
  'clientName', 'clientGST', 'clientAddress', 'clientState', 'clientStateCode', 'clientPIN', 'clientPhone', 'clientEmail', 'clientPAN',
  'location', 'category', 'vertical', 'placeOfSupply', 'placeOfSupplyStateCode',
  'gstType', 'cgstAmount', 'sgstAmount', 'igstAmount', 'cgstRate', 'sgstRate', 'igstRate', 'totalGST',
  'baseAmount', 'totalAmount', 'discountAmount', 'discountPercentage', 'roundOff', 'otherCharges', 'currency',
  'tdsApplicable', 'tdsPercentage', 'tdsAmount', 'tdsSection',
  'creditPeriod', 'dueDate', 'payDate', 'paymentMode', 'paymentTerms',
  'billingMonth', 'billingPeriodStart', 'billingPeriodEnd',
  'invoiceDescription', 'termsAndConditions', 'notes',
  'lineItems', 'totalLineItems',
  'irn', 'ackNo', 'ackDate', 'qrCode', 'einvoiceFiled', 'einvoiceDate',
  'invoiceStatus', 'paidAmount', 'pendingAmount', 'paidDate',
  'bookingStatus', 'bookingDate', 'bookingReason',
  'documentType', 'invoiceType', 'reverseCharge', 'exportInvoice', 'sezUnit',
  'rawData'
]

const EXTRACTED_INVOICE_EXPORT_DEFAULTS = {
  currency: 'INR',
  tdsApplicable: false,
  einvoiceFiled: false,
  invoiceStatus: 'Raised',
  paidAmount: 0,
  documentType: 'Invoice',
  reverseCharge: false,
  exportInvoice: false,
  sezUnit: false
}

function serializeRawDataForExport(raw) {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string') return raw
  try {
    return JSON.stringify(raw, null, 2)
  } catch (e) {
    return String(raw)
  }
}

/** Default `rawData` when upload/parse fails (canonical export string). */
const INVOICE_EXTRACT_RAW_PARSE_FAILED = 'FAILED: PDF cannot be parsed'

function rawDataStringFromUploadFailure(res) {
  if (res && res.data && typeof res.data.data === 'string' && res.data.data.trim()) {
    return res.data.data.trim()
  }
  if (res && typeof res.data === 'string' && res.data.trim()) {
    return res.data.trim()
  }
  const m = res?.data?.message || res?.message
  if (typeof m === 'string' && m.trim()) {
    return m.trim()
  }
  return INVOICE_EXTRACT_RAW_PARSE_FAILED
}

function buildExtractedDataForJsonDownload(src) {
  const merged = src && typeof src === 'object' ? src : {}
  const out = {}
  for (const key of EXTRACTED_INVOICE_EXPORT_KEYS) {
    if (key === 'rawData') {
      out[key] = Object.prototype.hasOwnProperty.call(merged, 'rawData')
        ? serializeRawDataForExport(merged.rawData)
        : null
      continue
    }
    if (key === 'lineItems') {
      const li = merged.lineItems
      out[key] = Array.isArray(li) ? li.map(item => (item && typeof item === 'object' ? { ...item } : item)) : []
      continue
    }
    const has = Object.prototype.hasOwnProperty.call(merged, key)
    const v = has ? merged[key] : undefined
    if (v === undefined || v === null || v === '') {
      out[key] = Object.prototype.hasOwnProperty.call(EXTRACTED_INVOICE_EXPORT_DEFAULTS, key)
        ? EXTRACTED_INVOICE_EXPORT_DEFAULTS[key]
        : null
      continue
    }
    out[key] = v
  }
  return out
}

export default {
  name: 'CreateInvoice',
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormFile,
    BFormCheckbox,
    BTable,
    BSpinner,
    BModal,
    VBModal,
    BAlert,
    BCollapse,
    vSelect,
    flatPickr,
    ToastificationContent,
  },
  directives: {
    'b-modal': VBModal,
    'b-toggle': VBToggle
  },
  data() {
    return {
      currentStep: 0, // Step 0: Options, Step 1: Upload PDF, Step 2: Form
      selectedOption: null, // 'create' or 'upload'
      loading: false,
      invoiceId: '',
      invoiceDate: '',
      invoiceCategory: null,
      invoiceLocation: '',
      invoiceVertical: null,
      invoiceSattvaGST: null,
      invoiceBillingMonth: null,
      invoiceClient: null,
      invoiceClientGST: null,
      invoiceGST: '',
      invoicePONumber: null,
      invoiceDescription: null,
      invoiceFile: null,
      supportingDocument: null,
      supportingDocAccept: INVOICE_CREATE_ACCEPT_ATTR,
      invoiceDocumentType: 'Tax Invoice',
      invoiceDocumentTypeOptions: INVOICE_DOCUMENT_TYPE_SELECT_OPTIONS,
      parsingError: null,
      submitError: '',
      extractedData: null, // Store extracted data from PDF
      dataExtractedFromUpload: false, // Flag to track if data was extracted from upload vs loaded from storage

      // Sattva Company Info (from master table API)
      sattvaCompanyName: '',
      sattvaAddress: '',
      sattvaPAN: '',
      sattvaBankName: '',
      sattvaAccountType: null,
      sattvaAccountName: '',
      sattvaIFSC: '',
      sattvaSACCode: null,
      sattvaLUT: false,
      accountTypeOptions: [],
      sacCodeOptions: [],

      // Client Info
      clientAddress: '',
      placeOfSupply: null,
      clientState: '',
      clientLegalName: '',
      clientEInvoiceAddress: '',

      // Rate Card
      showRateCard: false,
      selectedPaymentModel: null,
      paymentModelOptions: [],
      fixedInvoicePercentage: null,
      rateCardData: null,

      // Collect N Pay
      isCollectNPay: false,
      isCollectNPayApproved: null,
      payDate: null,
      creditPeriod: null,
      creditPeriodOptions: [],

      // TDS
      tdsApplicable: false,
      tdsPercentage: null,
      tdsAmount: 0,
      tdsPercentageOptions: [],

      // Collapse states (sections collapsed by default to shorten form)
      sattvaCompanyOpen: false,
      clientDetailsOpen: true, // Address, Place of Supply, State, PO - expanded by default
      paymentTdsOpen: false,
      extractedSummaryOpen: false,

      // GST Rates (from config API)
      gstRates: {
        sgst: 0,
        cgst: 0,
        igst: 0
      },

      // Dropdowns (from config API)
      categories: [],
      locations: [],
      clients: [],
      verticalOptions: [],

      // Invoice GST Options
      invoiceGSTOptions: [
        { title: 'SEZ', label: 'SEZ' },
        { title: 'IGST', label: 'IGST' },
        { title: 'SGST-CGST', label: 'SGST-CGST' }
      ],

      // Preview
      showPreviewModal: false,

      // Candidate List
      showInvoiceCandidateList: false,
      invoiceCandidateList: [],
      tagCandidateLineIndex: null,
      submitAction: 'submit_approval',
      showInvoiceCandidateFields: [
        { key: 'candidate_name', label: 'Candidate Name' },
        { key: 'position_name', label: 'Position Name' },
        { key: 'base_amount', label: 'Base Amount' }
      ],
      showInvoiceCandidateFieldsRPO: [
        { key: 'employee_id', label: 'Employee ID' },
        { key: 'employee_name', label: 'Employee Name' },
        { key: 'doj', label: 'DOJ' },
        { key: 'bu', label: 'BU' },
        { key: 'geo', label: 'Geo' },
        { key: 'base_amount', label: 'Base Amount' }
      ],
      candidateList: [],
      modalCandidateListFields: [
        { key: 'selected', label: 'Selected', sortable: true },
        { key: 'candidate_name', label: 'Name', sortable: true },
        { key: 'position_name', label: 'Position Name', sortable: true },
        { key: 'client', label: 'Client', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'joining_date', label: 'DOJ', sortable: true }
      ],
      selected: [],

      // Amounts
      totalBaseAmount: 0.0,
      totalGST: 0.0,
      totalAmountWithGST: 0.0,
      totalAmountWithGSTTDS: 0.0,

      // Validation
      error: {
        invoiceId: '',
        invoiceDate: '',
        invoiceLocation: '',
        invoiceVertical: '',
        invoiceCategory: '',
        invoiceClient: '',
        invoiceClientGST: '',
        invoiceGST: '',
        invoiceFile: '',
        supportingDocument: ''
      }
    }
  },
  computed: {
    gstTypeSuggestion() {
      const sattvaCode = (this.extractedData && this.extractedData.sattvaStateCode) || null
      const clientCode = (this.extractedData && this.extractedData.clientStateCode) || null
      if (!sattvaCode || !clientCode) return null
      // If first 2 digits of GST match → same state → SGST-CGST; else IGST
      const sattvaState = String(sattvaCode).substring(0, 2)
      const clientState = String(clientCode).substring(0, 2)
      return sattvaState === clientState ? 'SGST-CGST' : 'IGST'
    },
    isRpoTemplate() {
      return invoiceConfigService.getClientInvoiceLineItemTemplate(
        this.invoiceClient,
        this.invoiceCategory
      ) === 'rpo'
    },
    effectiveCandidateFields() {
      const base = this.isRpoTemplate ? this.showInvoiceCandidateFieldsRPO : this.showInvoiceCandidateFields
      return [...base, { key: '_actions', label: '', tdClass: 'text-nowrap', thStyle: { width: '72px' } }]
    },
    previewCandidateFields() {
      return this.effectiveCandidateFields.filter(f => f.key !== '_actions')
    },
    needsCandidateLines() {
      return this.invoiceCategory === 'Recruitment' || this.invoiceCategory === 'Contractual'
    },
    invoiceCandidateListHasValidLines() {
      if (!this.needsCandidateLines) return true
      if (!this.invoiceCandidateList.length) return false
      const sum = this.invoiceCandidateList.reduce((a, c) => a + parseFloat(c.base_amount || 0), 0)
      if (sum <= 0) return false
      for (const c of this.invoiceCandidateList) {
        if (c.isManualLine) {
          const name = this.isRpoTemplate
            ? String(c.employee_name || c.candidate_name || '').trim()
            : String(c.candidate_name || '').trim()
          if (!name) return false
        }
      }
      return true
    },
    billingMonthOptions() {
      // Current IST month + previous 6. Keep an extracted future month selected
      // without offering other future months.
      return buildBillingMonthOptions(this.invoiceBillingMonth)
    },
    calculateDueDate() {
      if (this.payDate && this.creditPeriod) {
        return moment(this.payDate).add(this.creditPeriod, 'days').format('YYYY-MM-DD')
      }
      return ''
    },
    /** Same payload as Download JSON (canonical shape + key order). */
    extractedDataCanonicalJson() {
      if (!this.extractedData) return ''
      const extractedData = buildExtractedDataForJsonDownload(this.extractedData)
      const invId = extractedData.invoiceId
      const metaInvoiceId = invId != null && invId !== '' ? invId : 'Draft'
      return JSON.stringify({
        metadata: {
          exportedAt: new Date().toISOString(),
          invoiceId: metaInvoiceId,
          fileName: extractedData.fileName || 'Unknown',
          totalFieldsExtracted: EXTRACTED_INVOICE_EXPORT_KEYS.length
        },
        extractedData
      }, null, 2)
    },
    extractedDataCanonicalFieldCount() {
      if (!this.extractedData) return 0
      return EXTRACTED_INVOICE_EXPORT_KEYS.length
    },
    isFormValid() {
      const hasInvoiceId = this.invoiceId && this.invoiceId.includes('SH')
      const hasInvoiceDate = !!this.invoiceDate
      const hasInvoiceLocation = !!this.invoiceLocation
      const hasInvoiceVertical = !!this.invoiceVertical
      const hasInvoiceCategory = !!this.invoiceCategory
      const hasInvoiceClient = !!this.invoiceClient
      const hasInvoiceClientGST = !!this.invoiceClientGST
      const hasInvoiceGST = !!this.invoiceGST
      const hasInvoiceFile = !!this.invoiceFile
      const linesOk = !this.needsCandidateLines || this.invoiceCandidateListHasValidLines

      return hasInvoiceId &&
        hasInvoiceDate &&
        hasInvoiceLocation &&
        hasInvoiceVertical &&
        hasInvoiceCategory &&
        hasInvoiceClient &&
        hasInvoiceClientGST &&
        hasInvoiceGST &&
        hasInvoiceFile &&
        linesOk
    }
  },
  watch: {
    invoiceLocation(newLocation) {
      if (newLocation) {
        this.loadMasterTableData(newLocation)
      }
    },
    invoiceClient(newClient) {
      if (newClient) {
        this.loadClientMasterData(newClient)
        if (this.invoiceVertical && this.invoiceCategory) {
          this.loadRateCard()
        }
      }
    },
    invoiceVertical() {
      if (this.invoiceClient && this.invoiceVertical && this.invoiceCategory) {
        this.loadRateCard()
      }
    },
    invoiceCategory() {
      if (this.invoiceClient && this.invoiceVertical && this.invoiceCategory) {
        this.loadRateCard()
      }
    },
    invoiceGST() {
      this.calculateAmounts()
    },
    tdsPercentage() {
      this.calculateAmounts()
    },
    tdsApplicable() {
      this.calculateAmounts()
    }
  },
  async created() {
    await this.loadConfigurations()
    await this.loadClients()
    await this.loadLocations()

    // Check route params: upload -> step 1 (PDF upload), create -> step 0 (option selection)
    if (this.$route.query?.action === 'upload') {
      this.selectedOption = 'upload'
      this.currentStep = 1
    } else if (this.$route.query?.action === 'create') {
      this.selectedOption = 'create'
      this.currentStep = 0
    }

    if (this.$route.query) {
      if (this.$route.query.client) this.invoiceClient = this.$route.query.client
      if (this.$route.query.category) this.invoiceCategory = this.$route.query.category
      if (this.$route.query.cid != null && this.$route.query.jid != null) {
        const baseAmt = parseFloat((parseFloat(this.$route.query.revenue) || 0).toFixed(2))
        const data = {
          cid: this.$route.query.cid,
          jid: this.$route.query.jid,
          candidate_name: this.$route.query.candidate_name || 'N/A',
          position_name: this.$route.query.position_name || 'N/A',
          base_amount: baseAmt
        }
        this.invoiceCandidateList.push(data)
        this.showInvoiceCandidateList = true
        this.calculateTotalBaseAmount()
      }
    }
  },
  methods: {
    isValidExtractedValue(val) {
      if (val == null || val === '') return false
      const s = String(val).trim().toLowerCase()
      if (s === 'not found' || s === 'n/a') return false
      return true
    },
    isValidDate(val) {
      if (!this.isValidExtractedValue(val)) return false
      const d = new Date(val)
      return !isNaN(d.getTime())
    },
    async loadConfigurations() {
      try {
        // Load all configurations from API
        const [
          creditPeriodsRes,
          tdsPercentagesRes,
          gstRatesRes,
          paymentModelsRes,
          documentTypesRes
        ] = await Promise.all([
          invoiceConfigService.getCreditPeriods(),
          invoiceConfigService.getTDSPercentages(),
          invoiceConfigService.getGSTRates(),
          invoiceConfigService.getPaymentModels(),
          invoiceConfigService.getDocumentTypes()
        ])

        if (creditPeriodsRes && creditPeriodsRes.status === 'OK') {
          this.creditPeriodOptions = creditPeriodsRes.data.map(p => ({
            text: `${p} days`,
            value: p
          }))
        }

        if (tdsPercentagesRes && tdsPercentagesRes.status === 'OK') {
          // Backend currently returns an empty list here (genuinely not
          // configured yet, not a fetch error) — fall back to a disabled
          // placeholder so the native <select> doesn't render as a bare,
          // seemingly-broken empty box.
          this.tdsPercentageOptions = tdsPercentagesRes.data.length
            ? tdsPercentagesRes.data.map(p => ({ text: `${p}%`, value: p }))
            : [{ text: 'No TDS percentages configured yet', value: null, disabled: true }]
        }

        if (gstRatesRes && gstRatesRes.status === 'OK') {
          this.gstRates = gstRatesRes.data
        }

        if (paymentModelsRes && paymentModelsRes.status === 'OK') {
          this.paymentModelOptions = paymentModelsRes.data.length
            ? paymentModelsRes.data.map(m => ({ text: m.label, value: m.value }))
            : [{ text: 'No payment models configured yet', value: null, disabled: true }]
        }

        // Load categories (from existing or config API)
        // For now using existing categories structure
        this.categories = [
          { title: 'Recruitment' },
          { title: 'Training' },
          { title: 'Anti Fraud' },
          { title: 'Contractual' },
          { title: 'Payroll' },
          { title: 'BGV' },
          { title: 'Miscellaneous' }
        ]

        // Load verticals (from config API if available)
        this.verticalOptions = [
          { title: 'Service Industry' },
          { title: 'Infra Industry' }
        ]
      } catch (error) {
        console.error('Error loading configurations:', error)
      }
    },
    async loadMasterTableData(locationId) {
      // Clear stale data from a previously-selected location up front — these are
      // readonly fields, so if the lookup below can't resolve anything, they should
      // read as genuinely blank/unavailable rather than showing a stale value.
      this.sattvaCompanyName = ''
      this.sattvaAddress = ''
      this.sattvaPAN = ''
      this.sattvaBankName = ''
      this.sattvaAccountName = ''
      this.sattvaIFSC = ''
      this.sattvaLUT = false
      this.invoiceSattvaGST = null
      try {
        // The "Invoice Location" dropdown (this.locations, from locationService.getAll())
        // is reduced to its display `title` for v-model/display/submit purposes, but any
        // locationId-based lookup expects the record's numeric `id`, not the display name.
        // BUG FIX (2026-07-17): this used to silently fall through to the raw name when no
        // match was found, which produced a real, confirmed 400:
        // `GET invoice/client-master-data?locationId=Pune`. Try a couple of field-name
        // aliases; if nothing resolves to an actual id, skip the lookup rather than firing
        // a request we already know is malformed.
        const matchedLocation = this.locations.find(
          loc => loc && (loc.title === locationId || loc.name === locationId || loc.label === locationId),
        )
        const resolvedLocationId = matchedLocation
          ? (matchedLocation.id ?? matchedLocation.location_id ?? matchedLocation.locationId ?? matchedLocation.ID ?? null)
          : null
        if (resolvedLocationId == null) {
          console.warn(`[CreateInvoice] Could not resolve a numeric location id for "${locationId}" — skipping Sattva company-details lookup instead of sending a malformed request.`)
          return
        }
        // Calls the dedicated `invoice/config/sattva-company-details` endpoint — see
        // getSattvaCompanyDetails()'s jsdoc in invoice-config-service.js. NO LONGER falls
        // back to the client-master-data endpoint for Sattva's own info (that was the
        // conceptually-wrong, 400-prone bug reported this session); if the dedicated
        // endpoint isn't live yet this resolves to SERVICE_UNAVAILABLE and the fields
        // above are simply left blank instead.
        // Dedicated endpoint preferred; service falls back to invoice/config
        // company info on 404 (see backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §18).
        const res = await invoiceConfigService.getSattvaCompanyDetails(resolvedLocationId)
        if (res && res.status === 'OK' && res.data) {
          const data = res.data
          this.sattvaCompanyName = data.companyName || ''
          this.sattvaAddress = data.registeredAddress || data.address || ''
          this.sattvaPAN = data.pan || ''
          this.sattvaBankName = data.bankName || ''
          this.sattvaAccountName = data.accountName || ''
          this.sattvaIFSC = data.ifscCode || ''
          this.sattvaLUT = data.lut || !!data.lutNo || false
          this.invoiceSattvaGST = data.gstin || data.gst || null

          if (data.accountTypes) {
            this.accountTypeOptions = data.accountTypes.map(t => ({
              text: t,
              value: t
            }))
          }

          if (data.sacCodes) {
            this.sacCodeOptions = data.sacCodes.map(code => ({
              text: `${code.code} - ${code.description}`,
              value: code.code
            }))
          }
        }
      } catch (error) {
        // Dedicated + fallback empty — leave fields blank (no console spam).
      }
    },
    async loadClientMasterData(clientName) {
      if (!clientName) return
      try {
        const res = await invoiceConfigService.getClientMasterData(clientName)
        if (res && res.status === 'OK' && res.data) {
          const d = res.data
          // Map actual master-table columns → invoice form fields, via
          // normalizeClientMasterData()'s wide key-alias matching (snake_case
          // AND camelCase variants) rather than reading `d.gstin_no` etc.
          // directly — the exact live field names for this route haven't
          // been confirmed against an authenticated response in this
          // environment, so this hedges against an unanticipated shape.
          // Only auto-fill if the field is currently empty (don't overwrite user input or PDF-extracted data)
          const n = normalizeClientMasterData(d)
          if (!this.invoiceClientGST)  this.invoiceClientGST  = n.gstin
          if (!this.clientAddress)     this.clientAddress     = n.address
          if (!this.clientState)       this.clientState       = n.state
          if (!this.placeOfSupply)     this.placeOfSupply     = n.placeOfSupply
          if (!this.invoicePONumber)   this.invoicePONumber   = n.poNumber
          if (!this.creditPeriod && n.creditPeriod) this.creditPeriod = String(n.creditPeriod)
          if (!this.invoiceVertical && n.vertical)   this.invoiceVertical = n.vertical
          // TDS: auto-enable if client has a configured TDS rate
          if (n.tdsRate && parseFloat(n.tdsRate) > 0) {
            this.tdsApplicable = true
            this.tdsPercentage = String(n.tdsRate)
          }
          // e-Invoice address stored separately — available for PDF/backend use
          if (n.einvoiceAddress) this.clientEInvoiceAddress = n.einvoiceAddress
          if (n.legalName) this.clientLegalName = n.legalName

          // Clients with multiple legal entities/office locations (e.g. "HPE" has
          // 5 rows for different GSTINs/addresses) can cause the backend to return
          // a blank/placeholder row instead of one with real data — there's no
          // known endpoint to fetch all rows for a clientName so we can't offer a
          // picker (see isClientMasterDataIncomplete() jsdoc). Warn instead of
          // silently leaving the invoice with blank GST/address.
          if (isClientMasterDataIncomplete(d)) {
            // eslint-disable-next-line no-console
            console.warn('[CreateInvoice] client-master-data looks incomplete for', clientName, '— raw response keys:', Object.keys(d || {}), 'raw response:', d)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'GST/address not auto-filled',
                text: `Could not auto-fill GST/address for "${clientName}" — this client may have multiple registered entities/locations, or the field names in the API response changed. Please enter GSTIN, address and state manually (see browser console for the raw API response).`,
                icon: 'AlertCircleIcon',
                variant: 'warning'
              }
            })
          }
        }
      } catch (e) {
        console.error('[CreateInvoice] loadClientMasterData error:', e)
      }
    },

    async loadRateCard() {
      if (!this.invoiceClient || !this.invoiceVertical || !this.invoiceCategory) {
        return
      }

      try {
        const res = await invoiceConfigService.getRateCard(
          this.invoiceClient,
          this.invoiceVertical,
          this.invoiceCategory
        )
        if (res && res.status === 'OK' && res.data) {
          this.rateCardData = res.data
          this.showRateCard = true
        } else {
          this.showRateCard = false
          this.rateCardData = null
          if (res?.status === 404) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Rate card not found',
                text: 'No rate card configured for this client/vertical/category. You can enter amounts manually.',
                icon: 'InfoIcon',
                variant: 'info'
              }
            })
          }
        }
      } catch (error) {
        console.error('Error loading rate card:', error)
        this.showRateCard = false
        this.rateCardData = null
        if (error?.response?.status === 404) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Rate card not found',
              text: 'No rate card configured for this client/vertical/category. You can enter amounts manually.',
              icon: 'InfoIcon',
              variant: 'info'
            }
          })
        }
      }
    },
    async loadClients() {
      try {
        const res = await clientService.getClients()
        if (res && res.data) {
          this.clients = res.data
        }
      } catch (error) {
        console.error('Error loading clients:', error)
      }
    },
    async loadLocations() {
      try {
        const res = await locationService.getAll()
        if (res && res.data) {
          this.locations = res.data
        }
      } catch (error) {
        console.error('Error loading locations:', error)
      }
    },
    populateFormFromExtractedData(extractedData) {
      // Basic Invoice Information (validate to avoid "Not found" / Invalid Date)
      if (this.isValidExtractedValue(extractedData.invoiceId)) this.invoiceId = extractedData.invoiceId
      if (this.isValidDate(extractedData.invoiceDate)) this.invoiceDate = new Date(extractedData.invoiceDate)
      if (this.isValidExtractedValue(extractedData.invoicePONumber)) this.invoicePONumber = extractedData.invoicePONumber
      if (this.isValidExtractedValue(extractedData.billingMonth)) this.invoiceBillingMonth = extractedData.billingMonth
      if (this.isValidExtractedValue(extractedData.invoiceDescription)) this.invoiceDescription = extractedData.invoiceDescription

      // Sattva Company Info
      if (this.isValidExtractedValue(extractedData.sattvaGST)) this.invoiceSattvaGST = extractedData.sattvaGST
      if (this.isValidExtractedValue(extractedData.sattvaCompanyName)) this.sattvaCompanyName = extractedData.sattvaCompanyName
      if (this.isValidExtractedValue(extractedData.sattvaAddress)) this.sattvaAddress = extractedData.sattvaAddress
      if (this.isValidExtractedValue(extractedData.sattvaPAN)) this.sattvaPAN = extractedData.sattvaPAN
      if (this.isValidExtractedValue(extractedData.sattvaBankName)) this.sattvaBankName = extractedData.sattvaBankName
      if (this.isValidExtractedValue(extractedData.sattvaIFSC)) this.sattvaIFSC = extractedData.sattvaIFSC
      if (this.isValidExtractedValue(extractedData.sattvaSACCode)) this.sattvaSACCode = extractedData.sattvaSACCode

      // Client Information
      if (this.isValidExtractedValue(extractedData.clientGST)) this.invoiceClientGST = extractedData.clientGST
      if (this.isValidExtractedValue(extractedData.clientName) || this.isValidExtractedValue(extractedData.client)) {
        const clientName = extractedData.clientName || extractedData.client
        const matchedClient = this.clients.find(c =>
          c.title && c.title.toLowerCase() === clientName.toLowerCase()
        )
        if (matchedClient) {
          this.invoiceClient = matchedClient.title
        }
      }
      if (this.isValidExtractedValue(extractedData.clientAddress)) this.clientAddress = extractedData.clientAddress
      if (this.isValidExtractedValue(extractedData.clientState)) this.clientState = extractedData.clientState
      
      // Place of Supply
      if (this.isValidExtractedValue(extractedData.placeOfSupply)) {
        const matchedLocation = this.locations.find(loc =>
          loc.title && loc.title.toLowerCase() === extractedData.placeOfSupply.toLowerCase()
        )
        if (matchedLocation) {
          this.placeOfSupply = matchedLocation.title
        }
      }

      // Location
      if (this.isValidExtractedValue(extractedData.location)) {
        const matchedLocation = this.locations.find(loc =>
          loc.title && loc.title.toLowerCase() === extractedData.location.toLowerCase()
        )
        if (matchedLocation) {
          this.invoiceLocation = matchedLocation.title
        }
      }

      // Category
      if (this.isValidExtractedValue(extractedData.category)) {
        const matchedCategory = this.categories.find(cat =>
          cat.title && cat.title.toLowerCase() === extractedData.category.toLowerCase()
        )
        if (matchedCategory) {
          this.invoiceCategory = matchedCategory.title
        }
      }

      // Vertical
      if (this.isValidExtractedValue(extractedData.vertical)) {
        const matchedVertical = this.verticalOptions.find(v =>
          v.title && v.title.toLowerCase() === extractedData.vertical.toLowerCase()
        )
        if (matchedVertical) {
          this.invoiceVertical = matchedVertical.title
        }
      }

      // GST Information
      if (this.isValidExtractedValue(extractedData.gstType)) {
        this.invoiceGST = extractedData.gstType
      }
      
      // Update GST rates if available
      if (extractedData.cgstRate) this.gstRates.cgst = extractedData.cgstRate
      if (extractedData.sgstRate) this.gstRates.sgst = extractedData.sgstRate
      if (extractedData.igstRate) this.gstRates.igst = extractedData.igstRate

      // Financial Information
      if (extractedData.baseAmount) {
        this.totalBaseAmount = parseFloat(extractedData.baseAmount)
      } else if (extractedData.totalAmount && extractedData.totalGST) {
        // Back-calculate base amount
        this.totalBaseAmount = parseFloat((parseFloat(extractedData.totalAmount) - parseFloat(extractedData.totalGST)).toFixed(2))
      } else if (extractedData.totalAmount) {
        // Back-calculate base amount from total (assuming 18% GST)
        const total = parseFloat(extractedData.totalAmount)
        this.totalBaseAmount = parseFloat((total / 1.18).toFixed(2))
      }
      
      // Set GST amounts if available
      if (extractedData.totalGST) {
        this.totalGST = parseFloat(extractedData.totalGST)
      }
      
      // TDS Information
      if (extractedData.tdsApplicable !== undefined) {
        this.tdsApplicable = extractedData.tdsApplicable
      }
      if (extractedData.tdsPercentage) {
        this.tdsPercentage = parseFloat(extractedData.tdsPercentage)
      }
      if (extractedData.tdsAmount) {
        this.tdsAmount = parseFloat(extractedData.tdsAmount)
      }

      // Payment Terms
      if (extractedData.creditPeriod) {
        this.creditPeriod = parseInt(extractedData.creditPeriod)
      }
      if (this.isValidDate(extractedData.payDate)) {
        this.payDate = new Date(extractedData.payDate)
      }
      if (extractedData.dueDate) {
        // Due date is calculated, but we can use it if provided
      }

      if (extractedData.lineItems && Array.isArray(extractedData.lineItems) && extractedData.lineItems.length > 0) {
        this.invoiceCandidateList = extractedData.lineItems.map(it => {
          const baseAmt = parseFloat(it.base_amount || it.baseAmount || it.amount || it.revenue || 0)
          const cid = it.cid != null && it.cid !== '' ? it.cid : it.candidate_id
          const jid = it.jid != null && it.jid !== '' ? it.jid : it.job_id
          const cidStr = cid != null && cid !== '' ? String(cid) : ''
          const jidStr = jid != null && jid !== '' ? String(jid) : ''
          const isManual = !cidStr || !jidStr || cidStr === '0' || jidStr === '0' || it.isManualLine === true
          return {
            isManualLine: isManual,
            cid: cidStr || '0',
            jid: jidStr || '0',
            employee_id: it.employee_id || it.employeeId || cidStr || '',
            employee_name: it.employee_name || it.employeeName || it.candidate_name,
            candidate_name: it.candidate_name || it.employee_name || it.employeeName || '',
            doj: it.doj || it.joining_date,
            bu: it.bu || it.position_name,
            geo: it.geo || '',
            position_name: it.position_name || it.bu || '',
            base_amount: baseAmt
          }
        })
        this.showInvoiceCandidateList = true
      }

      // Calculate amounts after populating
      this.calculateAmounts()
      
      // Load master table data if location is set
      if (this.invoiceLocation) {
        this.loadMasterTableData(this.invoiceLocation)
      }
    },
    calculateTotalBaseAmount() {
      const sum = this.invoiceCandidateList.reduce((acc, c) => acc + parseFloat(c.base_amount || 0), 0)
      this.totalBaseAmount = parseFloat(sum.toFixed(2))
      this.calculateAmounts()
    },
    calculateAmounts() {
      const base = parseFloat(this.totalBaseAmount) || 0
      let gstAmount = 0
      if (this.invoiceGST === 'SGST-CGST') {
        gstAmount = base * (this.gstRates.sgst / 100) + base * (this.gstRates.cgst / 100)
      } else if (this.invoiceGST === 'IGST') {
        gstAmount = base * (this.gstRates.igst / 100)
      }

      this.totalGST = parseFloat(gstAmount.toFixed(2))
      this.totalAmountWithGST = parseFloat((base + this.totalGST).toFixed(2))

      if (this.tdsApplicable && this.tdsPercentage) {
        this.tdsAmount = parseFloat((base * (this.tdsPercentage / 100)).toFixed(2))
      } else {
        this.tdsAmount = 0
      }

      this.totalAmountWithGSTTDS = parseFloat((this.totalAmountWithGST - this.tdsAmount).toFixed(2))
    },
    formatCurrency(val) {
      const n = parseFloat(val) || 0
      return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    goToCreateInvoice() {
      // Redirect to manual invoice creation
      this.$router.push({ name: 'apps-invoice-create-manual' })
    },
    goToUploadInvoice() {
      // Proceed to PDF upload step
      this.selectedOption = 'upload'
      this.currentStep = 1
    },
    onFileChange(event) {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0]
        const sizeError = getInvoiceFileSizeError(file)
        if (sizeError) {
          this.invoiceFile = null
          this.error.invoiceFile = sizeError
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File too large',
              text: sizeError,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          if (event.target) event.target.value = ''
          return
        }
        this.invoiceFile = file
        this.error.invoiceFile = ''
        this.parsingError = null
        // Don't clear extractedData here - let user decide
        this.dataExtractedFromUpload = false // Reset flag until upload completes
      }
    },
    clearExtractedData() {
      this.extractedData = null
      this.dataExtractedFromUpload = false
      this.invoiceFile = null
      localStorage.removeItem('invoice_extracted_data')
      // Clear form fields
      this.invoiceId = ''
      this.invoiceDate = ''
      this.invoiceCategory = null
      this.invoiceLocation = ''
      this.invoiceVertical = null
      this.invoiceClient = null
      this.invoiceClientGST = null
      this.totalBaseAmount = 0
      this.totalGST = 0
      this.totalAmountWithGSTTDS = 0
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Cleared',
          text: 'Extracted data cleared. Please upload a new file.',
          icon: 'CheckCircleIcon',
          variant: 'info'
        }
      })
    },
    async processPDFUpload() {
      if (!this.invoiceFile) {
        this.error.invoiceFile = 'Please select a PDF file'
        return
      }
      
      this.loading = true
      this.parsingError = null
      await this.uploadFile()
    },
    /** Persist canonical 87-field shape + string rawData (matches finance JSON spec). */
    storeCanonicalExtractedFailure(rawDataStr) {
      if (!this.invoiceFile) return
      this.extractedData = buildExtractedDataForJsonDownload({
        extractedAt: new Date().toISOString(),
        fileName: this.invoiceFile.name,
        fileSize: this.invoiceFile.size,
        fileType: this.invoiceFile.type,
        rawData: rawDataStr || INVOICE_EXTRACT_RAW_PARSE_FAILED
      })
      localStorage.setItem('invoice_extracted_data', JSON.stringify(this.extractedData))
    },
    async uploadFile() {
      this.loading = true
      const file = this.invoiceFile
      if (!file) {
        this.parsingError = 'Please select a file'
        this.loading = false
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      try {
        const res = await invoiceService.upload(formData)
        console.log('File uploaded successfully', res)
        console.log('Full API Response:', JSON.stringify(res, null, 2))

        if (res.status === 'OK' && res.data?.data) {
          const parsedData = res.data.data
          if (typeof parsedData === 'string') {
            const rawStr = parsedData.trim() || INVOICE_EXTRACT_RAW_PARSE_FAILED
            this.storeCanonicalExtractedFailure(rawStr)
            this.parsingError = rawStr
            this.dataExtractedFromUpload = false
            this.loading = false
            this.currentStep = 2
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'PDF not parsed',
                text: rawStr,
                icon: 'AlertCircleIcon',
                variant: 'warning'
              }
            })
            return
          }
          const sanitize = (v) => {
            if (v == null || v === '') return null
            const s = String(v).trim().toLowerCase()
            if (s === 'not found' || s === 'n/a') return null
            return v
          }
          const sanitizeDate = (v) => {
            const val = sanitize(v)
            if (!val) return null
            const d = new Date(val)
            return isNaN(d.getTime()) ? null : val
          }
          // COMPREHENSIVE DATA EXTRACTION - Extract ALL possible fields (sanitize "Not found")
          const extractedData = {
            // Metadata
            extractedAt: new Date().toISOString(),
            fileName: this.invoiceFile.name,
            fileSize: this.invoiceFile.size,
            fileType: this.invoiceFile.type,
            
            // Basic Invoice Information
            invoiceId: sanitize(parsedData.invoiceId || parsedData.invoice_id || parsedData.invoiceNumber || parsedData.invoice_number) || null,
            invoiceDate: sanitizeDate(parsedData.invoiceDate || parsedData.invoice_date || parsedData.date) || null,
            invoicePONumber: sanitize(parsedData.invoicePONumber || parsedData.invoice_po_number || parsedData.poNumber || parsedData.po_number || parsedData.po || parsedData.workOrderNumber || parsedData.work_order_number || parsedData.woNumber || parsedData.wo_number) || null,
            invoiceNumber: parsedData.invoiceNumber || parsedData.invoice_number || null,
            invoiceSeries: parsedData.invoiceSeries || parsedData.invoice_series || null,
            
            // Sattva Company Information (Vendor/Supplier)
            sattvaGST: parsedData.sattvaGST || parsedData.sattva_gst || parsedData.vendorGST || parsedData.vendor_gst || parsedData.supplierGST || parsedData.supplier_gst || parsedData.ourGST || parsedData.our_gst || null,
            sattvaCompanyName: parsedData.sattvaCompanyName || parsedData.sattva_company_name || parsedData.vendorName || parsedData.vendor_name || parsedData.supplierName || parsedData.supplier_name || null,
            sattvaAddress: parsedData.sattvaAddress || parsedData.sattva_address || parsedData.vendorAddress || parsedData.vendor_address || parsedData.supplierAddress || parsedData.supplier_address || null,
            sattvaPAN: parsedData.sattvaPAN || parsedData.sattva_pan || parsedData.vendorPAN || parsedData.vendor_pan || parsedData.supplierPAN || parsedData.supplier_pan || null,
            sattvaState: parsedData.sattvaState || parsedData.sattva_state || parsedData.vendorState || parsedData.vendor_state || null,
            sattvaStateCode: parsedData.sattvaStateCode || parsedData.sattva_state_code || parsedData.vendorStateCode || parsedData.vendor_state_code || null,
            sattvaPIN: parsedData.sattvaPIN || parsedData.sattva_pin || parsedData.vendorPIN || parsedData.vendor_pin || null,
            sattvaPhone: parsedData.sattvaPhone || parsedData.sattva_phone || parsedData.vendorPhone || parsedData.vendor_phone || null,
            sattvaEmail: parsedData.sattvaEmail || parsedData.sattva_email || parsedData.vendorEmail || parsedData.vendor_email || null,
            sattvaBankName: parsedData.sattvaBankName || parsedData.sattva_bank_name || parsedData.vendorBankName || parsedData.vendor_bank_name || null,
            sattvaAccountNumber: parsedData.sattvaAccountNumber || parsedData.sattva_account_number || parsedData.vendorAccountNumber || parsedData.vendor_account_number || null,
            sattvaIFSC: parsedData.sattvaIFSC || parsedData.sattva_ifsc || parsedData.vendorIFSC || parsedData.vendor_ifsc || null,
            sattvaSACCode: parsedData.sattvaSACCode || parsedData.sattva_sac_code || parsedData.sacCode || parsedData.sac_code || null,
            
            // Client Information (Buyer/Customer)
            clientName: sanitize(parsedData.clientName || parsedData.client_name || parsedData.client || parsedData.buyerName || parsedData.buyer_name || parsedData.customerName || parsedData.customer_name || parsedData.customer) || null,
            clientGST: parsedData.gstNumber || parsedData.gst_number || parsedData.clientGST || parsedData.client_gst || parsedData.buyerGST || parsedData.buyer_gst || parsedData.customerGST || parsedData.customer_gst || null,
            clientAddress: parsedData.clientAddress || parsedData.client_address || parsedData.buyerAddress || parsedData.buyer_address || parsedData.customerAddress || parsedData.customer_address || null,
            clientState: parsedData.state || parsedData.clientState || parsedData.client_state || parsedData.buyerState || parsedData.buyer_state || parsedData.customerState || parsedData.customer_state || null,
            clientStateCode: parsedData.stateCode || parsedData.clientStateCode || parsedData.client_state_code || parsedData.buyerStateCode || parsedData.buyer_state_code || null,
            clientPIN: parsedData.pin || parsedData.clientPIN || parsedData.client_pin || parsedData.buyerPIN || parsedData.buyer_pin || parsedData.customerPIN || parsedData.customer_pin || null,
            clientPhone: parsedData.clientPhone || parsedData.client_phone || parsedData.buyerPhone || parsedData.buyer_phone || parsedData.customerPhone || parsedData.customer_phone || null,
            clientEmail: parsedData.clientEmail || parsedData.client_email || parsedData.buyerEmail || parsedData.buyer_email || parsedData.customerEmail || parsedData.customer_email || null,
            clientPAN: parsedData.clientPAN || parsedData.client_pan || parsedData.buyerPAN || parsedData.buyer_pan || parsedData.customerPAN || parsedData.customer_pan || null,
            
            // Location & Category
            location: sanitize(parsedData.location || parsedData.invoiceLocation || parsedData.invoice_location) || null,
            category: sanitize(parsedData.category || parsedData.invoiceCategory || parsedData.invoice_category) || null,
            vertical: sanitize(parsedData.vertical || parsedData.invoiceVertical || parsedData.invoice_vertical) || null,
            
            // Place of Supply
            placeOfSupply: parsedData.placeOfSupply || parsedData.place_of_supply || parsedData.pos || parsedData.placeOfSupplyState || parsedData.place_of_supply_state || null,
            placeOfSupplyStateCode: parsedData.placeOfSupplyStateCode || parsedData.place_of_supply_state_code || parsedData.posStateCode || parsedData.pos_state_code || null,
            
            // GST Information
            gstType: parsedData.gstType || parsedData.gst_type || parsedData.invoiceGST || parsedData.invoice_gst || parsedData.taxType || parsedData.tax_type || null,
            cgstAmount: parsedData.cgstAmount || parsedData.cgst_amount || parsedData.cgst || null,
            sgstAmount: parsedData.sgstAmount || parsedData.sgst_amount || parsedData.sgst || null,
            igstAmount: parsedData.igstAmount || parsedData.igst_amount || parsedData.igst || null,
            cgstRate: parsedData.cgstRate || parsedData.cgst_rate || null,
            sgstRate: parsedData.sgstRate || parsedData.sgst_rate || null,
            igstRate: parsedData.igstRate || parsedData.igst_rate || null,
            totalGST: parsedData.totalGST || parsedData.total_gst || parsedData.gst || parsedData.gstAmount || parsedData.gst_amount || null,
            
            // Financial Information
            baseAmount: parsedData.baseAmount || parsedData.base_amount || parsedData.subtotal || parsedData.subTotal || parsedData.taxableAmount || parsedData.taxable_amount || parsedData.amount || null,
            totalAmount: parsedData.totalAmount || parsedData.total_amount || parsedData.grandTotal || parsedData.grand_total || parsedData.total || null,
            discountAmount: parsedData.discountAmount || parsedData.discount_amount || parsedData.discount || null,
            discountPercentage: parsedData.discountPercentage || parsedData.discount_percentage || null,
            roundOff: parsedData.roundOff || parsedData.round_off || parsedData.roundOffAmount || parsedData.round_off_amount || null,
            otherCharges: parsedData.otherCharges || parsedData.other_charges || parsedData.additionalCharges || parsedData.additional_charges || null,
            currency: parsedData.currency || parsedData.currencyCode || parsedData.currency_code || 'INR',
            
            // TDS Information
            tdsApplicable: parsedData.tdsApplicable || parsedData.tds_applicable || parsedData.tdsRequired || parsedData.tds_required || false,
            tdsPercentage: parsedData.tdsPercentage || parsedData.tds_percentage || parsedData.tdsRate || parsedData.tds_rate || null,
            tdsAmount: parsedData.tdsAmount || parsedData.tds_amount || parsedData.tds || null,
            tdsSection: parsedData.tdsSection || parsedData.tds_section || null,
            
            // Payment Terms
            creditPeriod: parsedData.creditPeriod || parsedData.credit_period || parsedData.paymentTerms || parsedData.payment_terms || null,
            dueDate: parsedData.dueDate || parsedData.due_date || parsedData.paymentDueDate || parsedData.payment_due_date || null,
            payDate: parsedData.payDate || parsedData.pay_date || parsedData.paymentDate || parsedData.payment_date || null,
            paymentMode: parsedData.paymentMode || parsedData.payment_mode || parsedData.paymentMethod || parsedData.payment_method || null,
            paymentTerms: parsedData.paymentTerms || parsedData.payment_terms || parsedData.terms || null,
            
            // Billing Information
            billingMonth: parsedData.billingMonth || parsedData.billing_month || parsedData.billingPeriod || parsedData.billing_period || null,
            billingPeriodStart: parsedData.billingPeriodStart || parsedData.billing_period_start || parsedData.periodStart || parsedData.period_start || null,
            billingPeriodEnd: parsedData.billingPeriodEnd || parsedData.billing_period_end || parsedData.periodEnd || parsedData.period_end || null,
            
            // Description & Notes
            invoiceDescription: parsedData.invoiceDescription || parsedData.invoice_description || parsedData.description || parsedData.notes || parsedData.remarks || parsedData.narration || null,
            termsAndConditions: parsedData.termsAndConditions || parsedData.terms_and_conditions || parsedData.terms || parsedData.tnc || null,
            notes: parsedData.notes || parsedData.remarks || parsedData.additionalNotes || parsedData.additional_notes || null,
            
            // Line Items / Candidates (if available)
            lineItems: parsedData.lineItems || parsedData.line_items || parsedData.items || parsedData.candidates || parsedData.candidateList || parsedData.candidate_list || [],
            totalLineItems: parsedData.totalLineItems || parsedData.total_line_items || parsedData.itemCount || parsedData.item_count || null,
            
            // E-Invoice Information
            irn: parsedData.irn || parsedData.IRN || parsedData.invoiceReferenceNumber || parsedData.invoice_reference_number || null,
            ackNo: parsedData.ackNo || parsedData.ack_no || parsedData.acknowledgementNumber || parsedData.acknowledgement_number || null,
            ackDate: parsedData.ackDate || parsedData.ack_date || parsedData.acknowledgementDate || parsedData.acknowledgement_date || null,
            qrCode: parsedData.qrCode || parsedData.qr_code || parsedData.QRCode || null,
            einvoiceFiled: parsedData.einvoiceFiled || parsedData.einvoice_filed || parsedData.eInvoiceFiled || false,
            einvoiceDate: parsedData.einvoiceDate || parsedData.einvoice_date || parsedData.eInvoiceDate || null,
            
            // Status Information
            invoiceStatus: parsedData.invoiceStatus || parsedData.invoice_status || parsedData.status || 'Raised',
            paidAmount: parsedData.paidAmount || parsedData.paid_amount || parsedData.paid || 0,
            pendingAmount: parsedData.pendingAmount || parsedData.pending_amount || parsedData.pending || null,
            paidDate: parsedData.paidDate || parsedData.paid_date || parsedData.paymentDate || parsedData.payment_date || null,
            
            // Booking Information
            bookingStatus: parsedData.bookingStatus || parsedData.booking_status || null,
            bookingDate: parsedData.bookingDate || parsedData.booking_date || null,
            bookingReason: parsedData.bookingReason || parsedData.booking_reason || null,
            
            // Additional Metadata
            documentType: parsedData.documentType || parsedData.document_type || 'Invoice',
            invoiceType: parsedData.invoiceType || parsedData.invoice_type || parsedData.type || null,
            reverseCharge: parsedData.reverseCharge || parsedData.reverse_charge || parsedData.isReverseCharge || parsedData.is_reverse_charge || false,
            exportInvoice: parsedData.exportInvoice || parsedData.export_invoice || parsedData.isExport || parsedData.is_export || false,
            sezUnit: parsedData.sezUnit || parsedData.sez_unit || parsedData.sez || false,
            
            // API payload as string (same shape as Download JSON / finance spec)
            rawData: serializeRawDataForExport(res.data)
          }
          
          // Calculate missing amounts if possible
          if (!extractedData.totalGST && extractedData.baseAmount && extractedData.gstType) {
            if (extractedData.gstType === 'SGST-CGST' && extractedData.cgstRate && extractedData.sgstRate) {
              extractedData.cgstAmount = parseFloat((extractedData.baseAmount * (extractedData.cgstRate / 100)).toFixed(2))
              extractedData.sgstAmount = parseFloat((extractedData.baseAmount * (extractedData.sgstRate / 100)).toFixed(2))
              extractedData.totalGST = extractedData.cgstAmount + extractedData.sgstAmount
            } else if (extractedData.gstType === 'IGST' && extractedData.igstRate) {
              extractedData.igstAmount = parseFloat((extractedData.baseAmount * (extractedData.igstRate / 100)).toFixed(2))
              extractedData.totalGST = extractedData.igstAmount
            }
          }
          
          if (!extractedData.totalAmount && extractedData.baseAmount && extractedData.totalGST) {
            extractedData.totalAmount = parseFloat((parseFloat(extractedData.baseAmount) + parseFloat(extractedData.totalGST)).toFixed(2))
          }
          
          if (!extractedData.pendingAmount && extractedData.totalAmount && extractedData.paidAmount) {
            extractedData.pendingAmount = parseFloat((parseFloat(extractedData.totalAmount) - parseFloat(extractedData.paidAmount)).toFixed(2))
          }
          
          // Save comprehensive extracted data
          this.extractedData = extractedData
          this.dataExtractedFromUpload = true // Mark that data was extracted from upload
          
          // Save to localStorage as JSON
          localStorage.setItem('invoice_extracted_data', JSON.stringify(this.extractedData))
          
          // Populate form fields from extracted data
          this.populateFormFromExtractedData(extractedData)
          
          // Move to step 2 after successful extraction
          this.loading = false
          this.currentStep = 2
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: `PDF parsed successfully! Extracted ${Object.keys(extractedData).length} fields.`,
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
        } else {
          const rawMsg = rawDataStringFromUploadFailure(res)
          this.storeCanonicalExtractedFailure(rawMsg)
          this.parsingError = (res && res.data && res.data.message) || res?.message || rawMsg
          this.dataExtractedFromUpload = false
          this.loading = false
          this.currentStep = 2
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Could not parse PDF',
              text: this.parsingError,
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
        }
      } catch (error) {
        console.error('Error uploading file:', error)
        this.parsingError = 'Error uploading file. Please try again.'
        this.storeCanonicalExtractedFailure(INVOICE_EXTRACT_RAW_PARSE_FAILED)
        this.dataExtractedFromUpload = false
        this.loading = false
        this.currentStep = 2
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Upload failed',
            text: this.parsingError,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      }
    },
    addManualInvoiceLine() {
      if (!this.invoiceClient || !this.invoiceCategory) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Select client & category',
            text: 'Choose client and category before adding lines.',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        return
      }
      if (this.invoiceCategory !== 'Recruitment' && this.invoiceCategory !== 'Contractual') {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Not applicable',
            text: 'Manual billing lines are for Recruitment or Contractual invoices.',
            icon: 'InfoIcon',
            variant: 'info'
          }
        })
        return
      }
      const row = this.isRpoTemplate
        ? {
            isManualLine: true,
            cid: '0',
            jid: '0',
            employee_id: '',
            employee_name: '',
            candidate_name: '',
            doj: '',
            bu: '',
            geo: '',
            position_name: '',
            base_amount: 0
          }
        : {
            isManualLine: true,
            cid: '0',
            jid: '0',
            candidate_name: '',
            position_name: '',
            base_amount: 0
          }
      this.invoiceCandidateList.push(row)
      this.showInvoiceCandidateList = true
      this.calculateTotalBaseAmount()
    },
    removeManualInvoiceLine(index) {
      if (index < 0 || index >= this.invoiceCandidateList.length) return
      this.invoiceCandidateList.splice(index, 1)
      if (this.invoiceCandidateList.length === 0) {
        this.showInvoiceCandidateList = false
      }
      this.calculateTotalBaseAmount()
    },

    // Tag uses the same ATS "Select Candidates" modal (Candidate Lookup removed).
    openTagCandidate(index) {
      this.tagCandidateLineIndex = index
      this.getCandidateListModal()
    },
    getCandidateListModal() {
      if (!this.invoiceClient || !this.invoiceCategory) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Please select Client and Category first',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        return
      }

      candidateService.getCandidateJoinedListReadyForInvoice(this.invoiceClient, this.invoiceCategory).then(res => {
        if (res && res.status === 'OK' && Array.isArray(res.data)) {
          const selectedCandidateIds = new Set(
            this.invoiceCandidateList.map(item => `${item.cid}-${item.jid}`)
          )
          this.candidateList = res.data.filter(candidate => {
            const candidateKey = `${candidate.cid}-${candidate.jid}`
            return !selectedCandidateIds.has(candidateKey)
          })
          this.$refs.candidateModal.show()
        } else {
          const msg = (res && res.status === 500)
            ? 'No candidates with status Joined for this client/category. Add manual lines from the PDF (names + amounts); you can still raise the invoice.'
            : (res?.data?.message || 'Unable to load candidate list. Please try again.')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res && res.status === 500 ? 'No ATS rows' : 'Error',
              text: msg,
              icon: 'AlertCircleIcon',
              variant: res && res.status === 500 ? 'warning' : 'danger'
            }
          })
        }
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: 'Failed to load candidate list. Please check your connection and try again.',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      })
    },
    onRowSelected(items) {
      this.selected = items
    },
    addSelectedCandidates() {
      if (this.selected.length === 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'No Selection',
            text: 'Please select at least one candidate',
            icon: 'AlertCircleIcon',
            variant: 'warning'
          }
        })
        return
      }

      this.selected.forEach(candidate => {
        const baseAmt = parseFloat(candidate.revenue || 0)
        const candidateData = this.isRpoTemplate
          ? {
              isManualLine: false,
              cid: candidate.cid,
              jid: candidate.jid,
              employee_id: String(candidate.cid || candidate.candidate_id || ''),
              employee_name: candidate.candidate_name,
              candidate_name: candidate.candidate_name,
              doj: candidate.joining_date || candidate.doj || null,
              bu: candidate.position_name || candidate.bu || '',
              geo: candidate.geo || '',
              position_name: candidate.position_name,
              base_amount: parseFloat(baseAmt.toFixed(2))
            }
          : {
              isManualLine: false,
              cid: candidate.cid,
              jid: candidate.jid,
              candidate_name: candidate.candidate_name,
              position_name: candidate.position_name,
              base_amount: parseFloat(baseAmt.toFixed(2))
            }
        this.invoiceCandidateList.push(candidateData)
      })

      this.showInvoiceCandidateList = true
      this.calculateTotalBaseAmount()
      this.$refs.candidateModal.hide()
      this.clearSelected()
    },
    clearSelected() {
      if (this.$refs.selectableTable) {
        this.$refs.selectableTable.clearSelected()
      }
      this.selected = []
    },
    /**
     * Aggregate tagged ATS lines (real cid+jid) → revenue = sum(base_amount).
     * Manual lines (cid/jid 0) and drafts are skipped by the caller.
     */
    async syncTaggedCandidatesRevenueFromLines() {
      const totals = new Map()
      ;(this.invoiceCandidateList || []).forEach((c) => {
        if (!c || c.isManualLine) return
        const cid = c.cid != null ? String(c.cid).trim() : ''
        const jid = c.jid != null ? String(c.jid).trim() : ''
        if (!cid || !jid || cid === '0' || jid === '0') return
        const key = `${cid}-${jid}`
        const amt = parseFloat(c.base_amount) || 0
        const prev = totals.has(key) ? totals.get(key).revenue : 0
        totals.set(key, {
          cid,
          jid,
          revenue: parseFloat(((prev || 0) + amt).toFixed(2)),
        })
      })
      if (totals.size === 0) return { updated: 0, failed: 0 }
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const updatedBy = `[{"title" : "${userData.first_name || ''} ${userData.last_name || ''}", "emp_id": "${userData.emp_id || ''}"}]`
      const results = await Promise.all(
        Array.from(totals.values()).map(async ({ cid, jid, revenue }) => {
          try {
            const res = await candidateService.updateCandidateStatus(cid, jid, {
              updatedBy,
              revenue,
            })
            return !!(res && res.status === 'OK')
          } catch (err) {
            console.error('[CreateInvoice] ATS revenue sync failed', cid, jid, err)
            return false
          }
        }),
      )
      const updated = results.filter(Boolean).length
      return { updated, failed: results.length - updated }
    },
    validation() {
      this.error = {
        invoiceId: '',
        invoiceDate: '',
        invoiceLocation: '',
        invoiceVertical: '',
        invoiceCategory: '',
        invoiceClient: '',
        invoiceClientGST: '',
        invoiceGST: '',
        invoiceFile: '',
        supportingDocument: ''
      }

      let isValid = true

      if (!this.invoiceId || !this.invoiceId.includes('SH')) {
        this.error.invoiceId = 'Invoice ID is required and must contain SH'
        isValid = false
      }

      if (!this.invoiceDate) {
        this.error.invoiceDate = 'Invoice Date is required'
        isValid = false
      }

      if (!this.invoiceLocation) {
        this.error.invoiceLocation = 'Invoice Location is required'
        isValid = false
      }

      if (!this.invoiceVertical) {
        this.error.invoiceVertical = 'Vertical is required'
        isValid = false
      }

      if (!this.invoiceCategory) {
        this.error.invoiceCategory = 'Invoice Category is required'
        isValid = false
      }

      if (!this.invoiceClient) {
        this.error.invoiceClient = 'Client is required'
        isValid = false
      }

      if (!this.invoiceClientGST) {
        this.error.invoiceClientGST = 'Client GST is required'
        isValid = false
      }

      if (!this.invoiceGST) {
        this.error.invoiceGST = 'GST Type is required'
        isValid = false
      }

      if (!this.invoiceFile) {
        this.error.invoiceFile = 'Invoice PDF is required'
        isValid = false
      } else {
        const sizeError = getInvoiceFileSizeError(this.invoiceFile)
        if (sizeError) {
          this.error.invoiceFile = sizeError
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File too large',
              text: sizeError,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          isValid = false
        }
      }

      if (this.submitAction !== 'draft' && this.supportingDocument) {
        const supportSizeError = getInvoiceFileSizeError(this.supportingDocument)
        const supportTypeError = getInvoiceFileTypeError(this.supportingDocument)
        if (supportSizeError || supportTypeError) {
          this.error.supportingDocument = supportSizeError || supportTypeError
          this.supportingDocument = null
          this.$toast({
            component: ToastificationContent,
            props: {
              title: supportSizeError ? 'File too large' : 'File type not allowed',
              text: supportSizeError || supportTypeError,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          isValid = false
        }
      }

      if (this.needsCandidateLines && !this.invoiceCandidateListHasValidLines) {
        let text = 'For Recruitment / Contractual, add lines with a positive base amount. Use Add manual line if the person is not in ATS or is not Joined yet, and enter the name as on the PDF.'
        if (this.invoiceCandidateList.some(c => c.isManualLine)) {
          const bad = this.invoiceCandidateList.some(c => {
            if (!c.isManualLine) return false
            const n = this.isRpoTemplate ? (c.employee_name || c.candidate_name) : c.candidate_name
            return !String(n || '').trim()
          })
          if (bad) text = 'Each manual line must have a name (as on the invoice).'
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Line items required',
            text,
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        isValid = false
      }

      return isValid
    },
    saveDraft() {
      this.submitAction = 'draft'
      this.$nextTick(() => this.onSubmit())
    },
    saveDraftFromPreview() {
      this.submitAction = 'draft'
      this.showPreviewModal = false
      this.$nextTick(() => this.onSubmit())
    },
    confirmSubmitFromPreview() {
      this.submitAction = 'submit_approval'
      this.showPreviewModal = false
      this.onSubmit()
    },
    async onSubmit() {
      if (!this.validation()) {
        return
      }

      this.loading = true
      this.submitError = ''

      const formData = new FormData()
      const statusToSubmit = this.submitAction === 'draft' ? 'Draft' : 'Pending Approval'
      formData.append('invoice_id', this.invoiceId)
      formData.append('status', statusToSubmit)
      formData.append('invoice_date', moment(this.invoiceDate).format('YYYY-MM-DD'))
      formData.append('category', this.invoiceCategory)
      formData.append('location', this.invoiceLocation)
      formData.append('vertical', this.invoiceVertical)
      formData.append('sattva_gst', this.invoiceSattvaGST || '')
      formData.append('billing_month', this.invoiceBillingMonth || '')
      formData.append('client', this.invoiceClient)
      formData.append('client_gst', this.invoiceClientGST)
      formData.append('client_address', this.clientAddress || '')
      formData.append('client_legal_name', this.clientLegalName || '')
      formData.append('client_einvoice_address', this.clientEInvoiceAddress || '')
      formData.append('place_of_supply', this.placeOfSupply || '')
      formData.append('client_state', this.clientState || '')
      formData.append('gst_type', this.invoiceGST)
      formData.append('po_number', this.invoicePONumber || '')
      formData.append('description', this.invoiceDescription || '')
      formData.append('document_type', this.invoiceDocumentType || 'Tax Invoice')
      formData.append('base_amount', this.totalBaseAmount)
      formData.append('gst_amount', this.totalGST)
      formData.append('tds_amount', this.tdsAmount)
      formData.append('total_amount', this.totalAmountWithGSTTDS)
      if (this.invoiceFile) {
        formData.append('invoiceFile', this.invoiceFile)
      }

      // Collect N Pay
      if (this.isCollectNPay) {
        formData.append('is_collect_n_pay', true)
        formData.append('collect_n_pay_approved', this.isCollectNPayApproved ? '1' : '0')
        formData.append('pay_date', this.payDate ? moment(this.payDate).format('YYYY-MM-DD') : '')
        formData.append('credit_period', this.creditPeriod || '')
        formData.append('due_date', this.calculateDueDate || '')
      }

      // TDS
      if (this.tdsApplicable) {
        formData.append('tds_applicable', true)
        formData.append('tds_percentage', this.tdsPercentage || '')
      }

      // Candidates (ATS-linked or manual lines — backend may use cid/jid 0 + names for reconciliation)
      this.invoiceCandidateList.forEach((candidate, index) => {
        const cid = candidate.cid != null && candidate.cid !== '' ? String(candidate.cid) : '0'
        const jid = candidate.jid != null && candidate.jid !== '' ? String(candidate.jid) : '0'
        formData.append(`candidates[${index}][cid]`, cid)
        formData.append(`candidates[${index}][jid]`, jid)
        formData.append(`candidates[${index}][base_amount]`, candidate.base_amount)
        if (candidate.isManualLine) {
          const nm = this.isRpoTemplate
            ? (candidate.employee_name || candidate.candidate_name || '')
            : (candidate.candidate_name || '')
          formData.append(`candidates[${index}][candidate_name]`, nm)
          formData.append(`candidates[${index}][position_name]`, candidate.position_name || candidate.bu || '')
          formData.append(`candidates[${index}][manual_line]`, '1')
        }
      })

      try {
        const res = await invoiceService.create(formData)
        if (res.status === 'OK') {
          const isDraft = this.submitAction === 'draft'
          // On real submit (not draft): tagged ATS candidates get revenue =
          // line base_amount (summed per cid+jid). Same API as InvoiceCreateManual.
          if (!isDraft) {
            try {
              const sync = await this.syncTaggedCandidatesRevenueFromLines()
              if (sync.failed > 0) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: 'Invoice created — revenue sync incomplete',
                    text: `Invoice saved, but ${sync.failed} candidate revenue update(s) failed.`,
                    icon: 'AlertCircleIcon',
                    variant: 'warning',
                  },
                })
              }
            } catch (syncErr) {
              console.warn('[CreateInvoice] ATS revenue sync error:', syncErr)
            }
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              text: isDraft ? 'Invoice saved as Draft.' : 'Invoice submitted for approval!',
              icon: 'CheckCircleIcon',
              variant: 'success'
            }
          })
          this.$router.push({ name: 'apps-invoice-dashboard' })
        } else {
          this.submitError = res.message || 'Error creating invoice'
          this.loading = false
        }
      } catch (error) {
        console.error('Error creating invoice:', error)
        this.submitError = 'Error creating invoice. Please try again.'
        this.loading = false
      }
    },
    formatIndianNumber(num) {
      if (!num && num !== 0) return '0'
      const numStr = num.toString()
      const parts = numStr.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    },
    downloadExtractedData() {
      if (!this.extractedData) return

      const extractedData = buildExtractedDataForJsonDownload(this.extractedData)
      const invId = extractedData.invoiceId
      const metaInvoiceId = invId != null && invId !== '' ? invId : 'Draft'

      const exportData = {
        metadata: {
          exportedAt: new Date().toISOString(),
          invoiceId: metaInvoiceId,
          fileName: extractedData.fileName || this.extractedData.fileName || 'Unknown',
          totalFieldsExtracted: EXTRACTED_INVOICE_EXPORT_KEYS.length
        },
        extractedData
      }

      const dataStr = JSON.stringify(exportData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      const link = document.createElement('a')
      link.href = url
      const fileSlug = (metaInvoiceId === 'Draft' ? 'draft' : String(metaInvoiceId).replace(/[^\w.-]+/g, '_'))
      link.download = `invoice_extracted_data_${fileSlug}_${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Downloaded',
          text: 'Extracted data JSON downloaded successfully!',
          icon: 'CheckCircleIcon',
          variant: 'success'
        }
      })
    }
  },
  mounted() {
    // Load previously extracted data if available (but don't show success message)
    const savedData = localStorage.getItem('invoice_extracted_data')
    if (savedData) {
      try {
        this.extractedData = JSON.parse(savedData)
        this.dataExtractedFromUpload = false // Mark as loaded from storage, not from upload
        // Optionally restore form fields from saved data
        // this.populateFormFromExtractedData(this.extractedData)
      } catch (e) {
        console.error('Error loading saved extracted data:', e)
        localStorage.removeItem('invoice_extracted_data') // Remove corrupted data
      }
    }
  }
}
</script>